
import { ModelNotifier } from "./model-notifier";

import { INodeObject, NodeObject } from "./common/ah.mb.common.node-object";
import { IBasicCommandResponse } from "./ah.mb.client-notifications";
import { LinkProperty } from "./ah.mb.model-utils";
import { ModelUtils } from "./ah.mb.ui.model-utils";

import { FetchWrapper } from "../infrastructure/fetch-wrapper";
import { ICreateNodeTemplateResponse } from "./model-notifier/ah.mb.common.model-actions-handler";

import { INodeCommodities } from "./ah.mb.commodity-and-units-rec";
import { IExportNodeDataModel } from "../entities/export-node-data-model";

interface IValidateDownloadNodeDataResult {
    ok: boolean;
    response: string;
}

export interface INodeSearchOptions {
    nodeTypes?: string[];
    pathFilter?: string;
    searchPathAndName?: string;
    searchNameOnly?: string;
    recursive?: boolean;
}

export interface INodeName {
    name: string;
}


import { IKinkPoint, INodeDiagramPosition } from "./model-notifier/ah.mb.ui.model-observer";
import { IModelInfo } from "../entities/nu-model-info";

/**
 * Model services class
 */
export class modelServices {

    private readonly modelObservable: ModelNotifier;

    private rootApiUrl = "/api/1.0";
    private nodesApiUrl = this.rootApiUrl + "/nodes/";
    private diagramApiUrl = this.rootApiUrl + "/diagram";
    private saveDiagramStateUrl = this.diagramApiUrl + "/savestate";
    private modelTreeApiUrl = this.rootApiUrl + "/modeltree";

    private getNodeVariablesUrl = this.nodesApiUrl + "getnodevariabletree";
    private getNodeDataUrl = this.nodesApiUrl + "getnodedata";
    private getNodeDataValueUrl = this.nodesApiUrl + "getnodedatavalue";
    private setNodeDataUrl = this.nodesApiUrl + "setnodedata";
    private getLinkDataDataUrl = this.nodesApiUrl + "getlinkdata";
    private createNodeUrl = this.nodesApiUrl + "create";
    private renameNodeUrl = this.nodesApiUrl + "rename";

    private getNodeTypesUrl = this.nodesApiUrl + "getnodetypes";
    private getNodeTypeVariablesTreeUrl = this.nodesApiUrl + "getnodetypevariablestree";

    private downloadNodeDataUrl = this.nodesApiUrl + "downloadnodedata";
    private uploadNodeDataUrl = this.nodesApiUrl + "uploadnodedata";
    private validateDownloadNodeDataUrl = this.nodesApiUrl + "validatedownloadnodedata";

    private validationApiRootUrl = this.rootApiUrl + "/modelvalidation";
    private validateModelUrl = this.validationApiRootUrl;
    private fixModelProblemsUrl = this.validationApiRootUrl + "/fix";

    private updateSlopeCfsUrl = this.nodesApiUrl + "uploadslopecfs";

    private executeScriptUrl = this.rootApiUrl + "/commands/executescript";

    private nodesSearchAndReplaceQueryUrl = this.nodesApiUrl + "searchandreplacequery";
    private nodesSearchAndReplaceUrl = this.nodesApiUrl + "searchandreplace";

    private deleteNodesUrl = this.nodesApiUrl + "delete";

    // Generic success function


    // Constructor
    constructor(modelObservableNotifier: ModelNotifier) {

        this.modelObservable = modelObservableNotifier;
    }


    // Sends diagram scroll and scale to server
    saveDiagramState = (modelId, sectorPathAndName, xpos, ypos, scale): Promise<IBasicCommandResponse> => {

        var payload = {
            modelId: modelId,
            sectorPathAndName: sectorPathAndName,
            xpos: xpos,
            ypos: ypos,
            scale: scale
        };

        return FetchWrapper.sendCommandAndHandle(this.saveDiagramStateUrl,
            { method: "post", body: JSON.stringify(payload) },
            false);

    };

    // Get variables of a node
    getNodeVariables = (modelId: string, nodePathAndName: string): Promise<any> => {

        var url =
            `${this.getNodeVariablesUrl}?modelId=${modelId}&nodePathAndName=${nodePathAndName
            }&separateModifiedVariables=false`;
        url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());

    };

    // Gets variable value of a node
    getNodeData = (modelId, nodePathAndName, variableName): Promise<any> => {
        var url = this.getNodeDataUrl +
            "?modelId=" +
            modelId +
            "&nodePathAndName=" +
            nodePathAndName +
            "&variableName=" +
            variableName;

        url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());

        //$.get({
        //    url: url,
        //    global: false, // Suppress global notification of ajax event
        //    success: (data) => {
        //        // Don't need to show success event for this
        //        //eventObservableObj.fireEvent("serviceResponse", { success: true, response: data });
        //        nodeDataReceivedFunc(data);
        //    }
        //});
    };

    // Gets variable value of a node
    getNodeDataValue = (modelId, nodePathAndName, variableName): Promise<any> => {
        var url = this.getNodeDataValueUrl +
            "?modelId=" +
            modelId +
            "&nodePathAndName=" +
            nodePathAndName +
            "&variableName=" +
            variableName;

        url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());
    };

    // Sets variable value of a node
    // Async: returns true or false, with message
    setNodeData = (modelId, nodePathAndName, variableName, variableData): Promise<IBasicCommandResponse> => {
        var url = this.setNodeDataUrl;

        var model = {
            modelId: modelId,
            nodePathAndName: nodePathAndName,
            variableName: variableName,
            value: variableData
        };

        return FetchWrapper.sendCommandAndHandle(url, { method: "post", body: JSON.stringify(model) });

        //$.post({
        //    url: url,
        //    data: model, //JSON.stringify(model),
        //    async: true,
        //    global: false, // Suppress global notification of ajax event
        //    success: (data) => {
        //        setVariableResponseFunc(data.success, data.response);
        //        // Don't need to show success event for this
        //        //eventObservableObj.fireEvent("serviceResponse", { success: true, response: data });
        //        //nodeDataReceivedFunc(data);
        //    },
        //    error: (data) => {
        //        setVariableResponseFunc(false, data.responseJSON.message);
        //        //eventObservableObj.fireEvent("serviceResponse", { success: false, response: data });
        //        //return false;
        //    }
        //});
    };

    // Returns node types in a model
    getNodeTypes = (modelId: string, nodePathAndName: string): Promise<any> => {
        var url = this.getNodeTypesUrl + "?modelId=" + modelId + "&rootNodePathAndName=" + nodePathAndName;

        url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());

    };

    getNodeTypeVariablesTree = (modelId: string, nodeType: Array<string>): Promise<any> => {
        var url = this.getNodeTypeVariablesTreeUrl + "?modelId=" + modelId + "&nodeType=" + nodeType;
        url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());

    };

    // Downloads node data file
    downloadNodeData(model: IExportNodeDataModel) {

        const variableNamesCsv = model.variableNames.join(",");
        const theNodeTypes = model.nodeTypes.join(",");

        var url = new URL(this.downloadNodeDataUrl, document.location.href);

        url.searchParams.append("modelId", model.modelId);

        url.searchParams.append("nodePathAndName", model.nodePathAndName);
        url.searchParams.append("nodeType", theNodeTypes);
        url.searchParams.append("variableName", variableNamesCsv);
        url.searchParams.append("includeSubRegions", model.includeSubRegions.toString());
        model.tags.forEach(tag => url.searchParams.append("tags", tag));
        model.excludeTags.forEach(tag => url.searchParams.append("excludeTags", tag));
        url.searchParams.append("tagsAndOr", model.tagsAndOr);

        // Ajax does not allow file downloading, do it this way
        window.location.href = url.href;
    };

    // Downloads node data file
    validateDownloadNodeData(model: IExportNodeDataModel): Promise<IValidateDownloadNodeDataResult> {

        const variableNamesCsv = model.variableNames.join(",");
        const theNodeTypes = model.nodeTypes.join(",");

        var url = new URL(this.validateDownloadNodeDataUrl, document.location.href);
        url.searchParams.append("modelId", model.modelId);
        url.searchParams.append("nodePathAndName", model.nodePathAndName);
        url.searchParams.append("nodeType", theNodeTypes);
        url.searchParams.append("variableName", variableNamesCsv);
        url.searchParams.append("includeSubRegions", model.includeSubRegions.toString());
        model.tags.forEach(tag => url.searchParams.append("tags", tag));
        model.excludeTags.forEach(tag => url.searchParams.append("excludeTags", tag));
        url.searchParams.append("tagsAndOr", model.tagsAndOr);

        //url = encodeURI(url);

        return FetchWrapper.typedFetch<IValidateDownloadNodeDataResult>(url.href, { method: "get" })
            .then(response => response.json());
        //                .then(response => response.json());

    };

    // Upload csv node data function
    uploadNodeData = (modelId, formData, callback) => {

        var url = this.uploadNodeDataUrl + "/" + modelId;

        $.post({
            url: url,
            global: false, // Suppress global notification of ajax event
            data: formData,
            success: (data) => {
                callback(data.success, data.response);
            },
            error: (data) => {
                callback(false, data.responseJSON.message);
            },
            cache: false,
            contentType: false,
            processData: false
        });
    };


    getNodeSearchUrl(modelId: string, searchOpts: INodeSearchOptions) {

        var url = this.nodesApiUrl + "nodesearch?modelId=" + modelId;

        if (searchOpts.nodeTypes)
            url = url + "&nodeTypeNames=" + searchOpts.nodeTypes;

        if (searchOpts.pathFilter)
            url = url + "&path=" + searchOpts.pathFilter;

        if (searchOpts.searchPathAndName)
            url = url + "&pathAndName=" + searchOpts.searchPathAndName;

        if (searchOpts.searchNameOnly)
            url = url + "&name=";

        if (searchOpts.recursive !== undefined)
            url = url + "&recursive=" + searchOpts.recursive;

        return url;
    };


    // Validates a model
    validateModel = (modelId): Promise<IBasicCommandResponse> => {

        var url = this.validateModelUrl + "/" + modelId + "?validateNodeData=true";

        return FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json());

    };

    // Returns url for model validation as datatable url
    getModelValidationResultsAsDatatableUrl = (modelId) => {

        var url = this.validationApiRootUrl + "/" + modelId + "/asdatatable";
        return encodeURI(url);

    };

    // Fix problems func
    fixModelProblems = (modelId): Promise<IBasicCommandResponse> => {

        var url = this.fixModelProblemsUrl;

        var payload = { modelId: modelId };

        return FetchWrapper.sendCommandAndHandle(url, { method: "post", body: JSON.stringify(payload) });
    };

    // Retreives network diagram 
    getNetworkDiagram(modelId,
        sectorPathAndName,
        diagramType,
        groupExternalNodesBySector: boolean): Promise<any> {

        sectorPathAndName = encodeURIComponent(sectorPathAndName);

        var diagramUrl =
            `${this.diagramApiUrl}?modelId=${modelId}&sectorPathAndName=${sectorPathAndName}&diagramType=${diagramType}&groupExternalNodesBySector=${groupExternalNodesBySector}`;

        return FetchWrapper.fetch(diagramUrl, { method: "get" })
            .then(response => response.json());
    };

    /**
     * Gets the model tree ajax url
     * @param {} modelId 
     * @returns {} 
     */
    getModelTreeAjaxUrl = (modelId) => {
        var nodePlaceholder = "%rootNode%";

        var url = this.modelTreeApiUrl + "?modelId=" + modelId + "&rootNode=" + nodePlaceholder;

        return url;
    };

    // Gets a model tree
    getModelTree = (modelId, successCallback) => {

        var diagramUrl =
            this.modelTreeApiUrl + "/?modelId=" + modelId + "&rootNode=";

        diagramUrl = encodeURI(diagramUrl);

        FetchWrapper.fetch(diagramUrl, { method: "get" })
            .then(response => response.json())

            //$.get({ url: diagramUrl, global: false })
            .then(result => {
                successCallback(result);
            });
    };

    // Gets variable value of a node
    getLinkData = (modelId, sourceNodePathAndName, destNodePathAndName, dataReceivedFunc) => {
        var url = this.getLinkDataDataUrl +
            "?modelId=" +
            modelId +
            "&sourceNodePathAndName=" +
            sourceNodePathAndName +
            "&destNodePathAndName=" +
            destNodePathAndName;

        url = encodeURI(url);

        $.get({
            url: url,
            global: false,
            success: (data) => {
                // Don't need to show success event for this
                //eventObservableObj.fireEvent("serviceResponse", { success: true, response: data });
                dataReceivedFunc(data);
            }
        });
    };

    // Updates a model slopecfs
    updateSlopeCfs = (modelId: string, absoluteRise: number): Promise<IBasicCommandResponse> => {

        var url = this.updateSlopeCfsUrl;
        url = encodeURI(url);

        var payload = {
            modelId: modelId,
            absoluteRise: absoluteRise
        };

        var request = { method: "post", body: JSON.stringify(payload) }


        return FetchWrapper.sendCommandAndHandle(url, request);
    };

    // Auto-tags a model
    autoTagModel = (modelId: string, matchWholeNodeNamesOnly: boolean): Promise<IBasicCommandResponse> => {

        var url = "/api/1.0/nodetags/autotagmodel";
        url = encodeURI(url);

        var payload = {
            modelId,
            matchWholeNodeNamesOnly
        };

        var request = {
            method: "post",
            body: JSON.stringify(payload)
        }

        return FetchWrapper.sendCommandAndHandle(url, request);

    };

    // Clears a model's tags
    clearTags = (modelId): Promise<IBasicCommandResponse> => {

        return FetchWrapper.sendCommandAndHandle(
            encodeURI("/api/1.0/nodetags/cleartags"),
            {
                method: "post",
                body: JSON.stringify({
                    modelId: modelId,
                })
            });
    };


    // Save commands function, to save model changes
    executeScript = (formData, modelId) => {

        formData.append("modelId", modelId);

        var url = this
            .executeScriptUrl; // + '?name=' + name + '&description=' + desc + '&category=' + category + '&visibility=' + visibility;
        // url = encodeURI(url);

        return $.ajax({
            url: url,
            type: "POST",
            data: formData,
            cache: false,
            contentType: false,
            processData: false
        });
    };

    // Gets a model tree
    searchAndReplaceQuery = (modelId,
        rootNode,
        searchText,
        replaceText,
        targetType: string): Promise<any> => {

        var paramsObj = {
            modelId: modelId,
            rootNode: rootNode,
            searchText: searchText,
            replaceText: replaceText,
            targetType: targetType
        };

        var params = $.param(paramsObj, false);

        var url = this.nodesSearchAndReplaceQueryUrl + "?" + params;
        //url = encodeURI(url);

        return FetchWrapper.fetch(url, { method: "get" })
            .then((response) => response.json());

    };

    // Gets a model tree
    executeSearchAndReplace = (modelId,
        rootNode,
        searchText,
        replaceText,
        targetType: string): Promise<IBasicCommandResponse> => {

        var paramsObj = {
            modelId: modelId,
            rootNode: rootNode,
            searchText: searchText,
            replaceText: replaceText,
            targetType: targetType
        };

        var url = this.nodesSearchAndReplaceUrl;
        url = encodeURI(url);

        return FetchWrapper.sendCommandAndHandle(url, { method: "post", body: JSON.stringify(paramsObj) });
    };

    /**
     * Delete nodes
     * @param {} modelId 
     * @param {} nodes 
     * @param {} callback 
     * @returns {} 
     */
    deleteNodes = (modelId: string, nodes: NodeObject[], options?: { bypassRecycleBin: boolean }): Promise<IBasicCommandResponse> => {

        // Node is NodeObjects[]
        // Get names only
        var nodeNames = nodes.map((node) => {
            return node.pathAndName;
        });

        if (options)
            var byPassRB = options.bypassRecycleBin
        else
            byPassRB = false;

        var payload = {
            modelId: modelId,
            nodePathAndNames: nodeNames,
            byPassRecycleBin: byPassRB
        };

        var request = {
            method: "post",
            body: JSON.stringify(payload)
        }

        return FetchWrapper.sendCommandAndHandle(this.deleteNodesUrl, request)
            .then(data => {
                if (data.success)
                    this.modelObservable.notifyNodesDeleted(nodes);
                return data;
            });
    };

    groupNodes = (modelId: string, nodes: INodeObject[], groupName: string): Promise<IBasicCommandResponse> => {

        // Get names only
        var nodeNames = nodes.map((node) => {
            return node.pathAndName;
        });

        var payload = {
            modelId: modelId,
            nodes: nodeNames,
            group: groupName
        };

        return $.post({
            url: this.nodesApiUrl + "group",
            type: "POST",
            data: payload,
            success: (data: IBasicCommandResponse) => {
                if (data.success)
                    this.modelObservable.notifyNodesGrouped(nodes, groupName);
            }
        });
    };

    ungroupNodes = (modelId: string, node: string): Promise<IBasicCommandResponse> => {

        var payload = {
            modelId: modelId,
            groupNodePathAndName: node,
        };


        var url = this.nodesApiUrl + "ungroup";

        var request = {
            method: "POST",
            data: payload
        };

        var jqueryPromise =
            //$.post(request);
            FetchWrapper.fetch(url, request)
                .then(response => response.json());

        return jqueryPromise.then(() =>
            this.modelObservable.notifyNodesUngrouped(node));
        //if (jqueryPromise.success)
        //    return this.modelObservable.notifyNodesUngrouped(node);
        //else
        //    return Promise.reject();


        //.then((data: IBasicCommandResponse) => {
        //    if (data.success)
        //});

    };

    /**
     * Sets node positions
     */
    setNodePositions
        (modelId: string, positions: INodeDiagramPosition[]): Promise<IBasicCommandResponse> {
        if (positions.length > 0)
        {
            var payload =
            {
                modelId: modelId,
                positions: positions
            };

            return FetchWrapper.sendCommandAndHandle(`${this.nodesApiUrl}setnodepositions`,
                { method: "post", body: JSON.stringify(payload) },
                false);
        }

        return Promise.resolve<IBasicCommandResponse>({ success: true, response: "" });

    };

    /**
* Sets node positions
*/
    setKinkPoints(modelId: string, positions: IKinkPoint[]): Promise<IBasicCommandResponse> {
        var payload =
        {
            modelId: modelId,
            kinkPoints: positions
        };

        return FetchWrapper.sendCommandAndHandle(`/api/1.0/command/setkinkpoints`,
            { method: "post", body: JSON.stringify(payload) },
            false);
    };


    // Pastes nodes
    pasteNodes(sourceModelId: string,
        destModelId: string,
        nodeFullPaths: string[],
        targetPath: string,
        subNodesToInclude: string[],
        isCut: boolean): Promise<IBasicCommandResponse> {

        let url = this.nodesApiUrl + "paste";

        let payload = {
            sourceModelId: sourceModelId,
            destModelId: destModelId,
            nodesPathAndNames: nodeFullPaths,
            targetPath: targetPath,
            subNodesToInclude: subNodesToInclude,
            isCut: isCut
        };

        var request = { method: "POST", body: JSON.stringify(payload) };

        var fetch = FetchWrapper.sendCommandAndHandle(url, request, false, { modelId: destModelId })
            .then(data => {
                if (data.success) 
                    this.modelObservable.notifyNodesPasted(targetPath, nodeFullPaths, isCut);
                return data;
            });

        return fetch;

    };

    createLink = (modelId: string,
        sourceNodePathAndName: string,
        destNodePathAndName: string,
        commodity: string,
        units: string,
        properties: Array<LinkProperty>): Promise<any> => {

        var payload = {
            modelId: modelId,
            sourceNodePathAndName: sourceNodePathAndName,
            destNodePathAndName: destNodePathAndName,
            commodity: commodity,
            units: units,
            properties: properties
        };

        var request = { method: "post", body: JSON.stringify(payload) };

        return FetchWrapper.sendCommandAndHandle(this.nodesApiUrl + "createlink", request)

            //            return $.post(this.nodesApiUrl + "\\createlink", payload)
            .then((data: IBasicCommandResponse) => {
                if (data.success)
                    // Notify node observer
                    this.modelObservable.notifyLinkCreated(sourceNodePathAndName,
                        destNodePathAndName,
                        commodity,
                        units,
                        properties);
            });
    }

    // Creares a new node
    createNode = (modelId: string,
        parentNode: string,
        nodeName: string,
        nodeType: string,
        initData: any,
        successCallback): Promise<IBasicCommandResponse> => {

        var payload = {
            modelId: modelId,
            parentNodeName: parentNode,
            nodeName: nodeName,
            nodeType: nodeType,
            initData: initData
        };

        return FetchWrapper.sendCommandAndHandle(this.createNodeUrl,
            {
                method: "post",
                body: JSON.stringify(payload),

            })
            .then(data => {
                if (data.success) {
                    // Notify node observer
                    const nodeInfo = new NodeObject(parentNode + "\\" + nodeName, nodeType, false, "");
                    this.modelObservable.notifyNodeCreated(nodeInfo, initData);
                    successCallback();
                };
                return data;
            });

        //$.post({
        //    url: this.createNodeUrl,
        //    data: JSON.stringify(payload),
        //    contentType: "application/json",
        //    dataType: "json",
        //    success: (data) => {
        //        if (data.success) {
        //            // Notify node observer
        //            const nodeInfo = new NodeObject(parentNode + "\\" + nodeName, nodeType, false);
        //            this.modelObservable.notifyNodeCreated(nodeInfo, initData);
        //            successCallback();
        //        }
        //    }
        //});
    };

    // Creares a new node
    renameNode(modelId, nodePathAndName, newName): Promise<IBasicCommandResponse> {

        var payload = {
            modelId: modelId,
            nodePathAndName: nodePathAndName,
            newName: newName
        };
        var request = {
            method: "post",
            body: JSON.stringify(payload),
            headers: new Headers({
                'Accept': "application/json",
                'Content-Type': "application/json",
            })

        };

        return FetchWrapper.sendCommandAndHandle(this.renameNodeUrl, request)

            //            return $.post(this.renameNodeUrl, payload)
            .then(data => {

                if (data.success)
                    // Notify node observer
                    this.modelObservable.notifyNodeRenamed(nodePathAndName, newName);
                return data;
            });
    }

    /**
* Deletes a link
* @param sourceNode 
* @param destNode 
* @returns {} 
*/
    deleteLink(modelId: string, sourceNode: string, destNode: string): Promise<IBasicCommandResponse> {

        const payload = { modelId: modelId, sourceNodePathAndName: sourceNode, destNodePathAndName: destNode };
        const request = { method: "post", body: JSON.stringify(payload) };


        //return $.post(this.nodesApiUrl + "\\" + "deletelink",

        //    { modelId: modelId, sourceNodePathAndName: sourceNode, destNodePathAndName: destNode })

        return FetchWrapper.sendCommandAndHandle(this.nodesApiUrl + "deletelink", request)
            .then(response => {
                if (response.success)
                    this.modelObservable.notifyLinkDeleted(sourceNode, destNode);
                return response;
            });

    }

    editLink = (modelId: string,
        sourceNodePathAndName: string,
        destNodePathAndName: string,
        commodity: string,
        units: string,
        properties: Array<LinkProperty>): Promise<IBasicCommandResponse> => {

        var payload = {
            modelId: modelId,
            sourceNodePathAndName: sourceNodePathAndName,
            destNodePathAndName: destNodePathAndName,
            commodity: commodity,
            units: units,
            properties: properties
        };

        var request = { method: "post", body: JSON.stringify(payload) };

        return FetchWrapper.sendCommandAndHandle(this.nodesApiUrl + "editlink", request)
            .then((data => {
                if (data.success)
                    // Notify node observer
                    this.modelObservable.notifyLinkEdited(sourceNodePathAndName,
                        destNodePathAndName,
                        commodity,
                        units,
                        properties);
                return data;
            }));
    }

    createNodeTemplate(modelId: string, nodes: Object[], templateName: string):
        Promise<ICreateNodeTemplateResponse> {

        const nodePaths = nodes.map((p: NodeObject) => { return p.pathAndName });
        const payload = { modelId: modelId, templateName: templateName, nodes: nodePaths };
        const request = { method: "post", body: JSON.stringify(payload) };
        return FetchWrapper.sendCommandAndHandle("/api/1.0/nodes/createtemplate", request)
            .then(response => {

                // Figure out where the template is 
                // Note: hardcoded paths here
                var paths = ["Templates", templateName]; // ModelUtils.extractPathStackFromPathAndName(nodePaths[0]).slice(0, 1);
                //paths.push(templateName);
                const templatesPath = ModelUtils.buildPathAndNameFromSegments(paths);

                if (response.success) {
                    const nodeInfo = new NodeObject(templatesPath, "Template", true, "");
                    this.modelObservable.notifyNodeCreated(nodeInfo, null);
                }
                return { commandResponse: response, createdNodePathAndName: templatesPath }

            });
    }


    addNodeFromTemplate(modelId: string, parentNodePathAndName: string, templateNodePathAndName: string,
        initData: any) {
        const payload = {
            modelId: modelId,
            parentNodePathAndName: parentNodePathAndName,
            templateNodePathAndName: templateNodePathAndName,
            initData: initData

        };
        const request = { method: "post", body: JSON.stringify(payload) };
        return FetchWrapper.sendCommandAndHandle("/api/1.0/nodes/addfromtemplate", request)
            .then(data => {
                if (data.success) {
                    const createdNodeName = NodeObject.extractPath(templateNodePathAndName);
                    const createNodePath = NodeObject.extractPath(parentNodePathAndName);
                    const createdNodePathAndName = NodeObject.combinePathAndName(createNodePath, createdNodeName);
                    this.modelObservable.notifyNodesPasted(parentNodePathAndName, [createdNodePathAndName], false);
                }
                return data;
            });
    }

    getNodeCommodities(modelId: string, nodePathAndName: string): Promise<INodeCommodities> {

        return FetchWrapper.fetch(
            `/api/1.0/nodes/nodecommodities?modelId=${modelId}&nodePathAndName=${nodePathAndName}`,
            { method: "get" })
            .then(response => response.json())
            .then(response => {

                var inputs = response.inputCommodities.map(p => {
                    const commodity = p.item1, unit = p.item2;
                    return {
                        commodity,
                        unit
                    };
                });

                var outputs = response.outputCommodities.map(p => {
                    const commodity = p.item1, unit = p.item2;
                    return {
                        commodity,
                        unit
                    };
                });

                return { inputCommodities: inputs, outputCommodities: outputs };
            });
    }

    getNode(modelId: string, nodePathAndName: string): Promise<any> {
        return FetchWrapper.fetch(`/api/1.0/nodes/getnode?modelId=${modelId}&nodePathAndName=${nodePathAndName}`,
            { method: "get" })
            .then(response => response.json());

    }

    getModelNames(modelId: string): Promise<INodeName[]> {

        const url = this.getNodeSearchUrl(modelId,
            {
                nodeTypes: ["Model"],
                pathFilter: "\\Root",
                recursive: false
            });

        return FetchWrapper.fetch(url,
            { method: "get" })
            .then(response => response.json())
            .then(response => response.map(p => { return { name: NodeObject.extractName(p.name) } }));

    }

    getScenarioNames(modelId: string): Promise<INodeName[]> {

        // For now, no nested scenarios
        if (NodeObject.isScenarioModelId(modelId))
            return Promise.resolve([]);

        const url = `/api/1.0/query/GetModelScenarioNamesQuery?modelId=${modelId}`

        return FetchWrapper.fetch(url,
            { method: "get" })
            .then(response => response.json())
            .then(response => response.map(p => { return { name: NodeObject.extractName(p) } }));

    }

    getModelInfo(modelId: string): Promise<IModelInfo> {
        return FetchWrapper.typedFetch<IModelInfo>(`/api/1.0/query/getModelInfoExt/?modelId=${modelId}`)
            .then(response => response.json());

    }

}






// WEBPACK FOOTER //
// ./ClientApp/scripts/ah.mb.ui.model-services.ts